import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import {
  LayoutDashboard,
  Users,
  PackageOpen,
  History,
  BellRing,
  Wallet,
  LogOut,
  Menu,
  UserCog,
  Building2,
  BarChart3,
  Crown,
  Settings,
  Boxes,
  Wrench,
} from "lucide-react";
import { Toaster } from "./ui/sonner";

// Sıra şartname #8'e göre: "Hatırlatmalar" altında "Satış/Stok".
// Satış/Stok yalnızca pakette aktifse eklenir (şartname #10).
const BASE_NAV = [
  { to: "/", label: "Pano", icon: LayoutDashboard, end: true },
  { to: "/musteriler", label: "Müşteriler", icon: Users },
  { to: "/emanetler", label: "Emanetler", icon: PackageOpen },
  { to: "/hatirlatmalar", label: "Hatırlatmalar", icon: BellRing },
  { to: "/odemeler", label: "Ödemeler", icon: Wallet },
  { to: "/gecmis", label: "Geçmiş", icon: History },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3 },
];

const SALES_STOCK_NAV = { to: "/satis-stok", label: "Satış/Stok", icon: Boxes };

const OWNER_NAV = [
  { to: "/personel", label: "Personel", icon: UserCog },
  { to: "/abonelik", label: "Abonelik", icon: Crown },
  { to: "/ayarlar", label: "Ayarlar", icon: Settings },
];

function NavItem({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-4 py-3 border-l-2 transition-all ${
          isActive
            ? "border-l-amber-500 bg-foreground text-background"
            : "border-l-transparent text-muted-foreground hover:text-foreground hover:bg-muted"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="text-sm font-medium tracking-tight">{label}</span>
    </NavLink>
  );
}

export default function Layout() {
  const { user, logout, isOwner, salesStockEnabled, maintenanceMode } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // "Hatırlatmalar" hemen altına eklenir; paket pasifse menüde HİÇ görünmez.
  const baseNav = salesStockEnabled
    ? (() => {
        const idx = BASE_NAV.findIndex((i) => i.to === "/hatirlatmalar");
        const copy = [...BASE_NAV];
        copy.splice(idx + 1, 0, SALES_STOCK_NAV);
        return copy;
      })()
    : BASE_NAV;

  const NAV = isOwner ? [...baseNav, ...OWNER_NAV] : baseNav;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = ({ onItemClick }) => (
    <div className="flex flex-col h-full">
      <Link to="/" onClick={onItemClick} className="px-5 py-5 border-b border-border">
        <div className="flex items-center gap-3">
          <img
            src="/esnek-icon.png"
            alt="Esnek Yazılım"
            className="h-9 w-9 rounded-sm object-contain shrink-0"
          />
          <div>
            <div className="font-display font-black text-lg leading-none tracking-tighter">
              LASTİK OTELİ
            </div>
            <div className="label-eyebrow mt-1 text-amber-600">Yönetim Paneli</div>
          </div>
        </div>
        {user?.tenant_name && (
          <div className="mt-3 flex items-center gap-2 px-2 py-1.5 bg-muted rounded-sm">
            <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-semibold truncate" data-testid="tenant-name">{user.tenant_name}</span>
          </div>
        )}
      </Link>
      <nav className="flex-1 py-3 overflow-y-auto">
        {NAV.map((item) => (
          <NavItem key={item.to} {...item} onClick={onItemClick} />
        ))}
      </nav>
      <div className="border-t border-border p-4">
        <div className="mb-3 px-1">
          <div className="label-eyebrow">Oturum</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm font-semibold truncate" data-testid="current-user-name">
              {user?.name || "—"}
            </span>
            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${user?.role === "owner" ? "bg-amber-500 text-white" : "bg-muted text-muted-foreground"}`}>
              {user?.role === "owner" ? "Yönetici" : "Personel"}
            </span>
          </div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="w-full rounded-sm justify-start"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background grid-bg">
      <header className="lg:hidden sticky top-0 z-40 bg-foreground text-background border-b border-foreground">
        <div className="flex items-center justify-between px-4 h-14">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <img
              src="/esnek-icon.png"
              alt="Esnek Yazılım"
              className="h-6 w-6 rounded-sm object-contain shrink-0 bg-white p-0.5"
            />
            <div className="min-w-0">
              <div className="font-display font-black tracking-tighter text-sm leading-none">LASTİK OTELİ</div>
              {user?.tenant_name && <div className="text-[10px] text-amber-400 truncate mt-0.5">{user.tenant_name}</div>}
            </div>
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                className="p-2 hover:bg-foreground/80 rounded-sm"
                data-testid="mobile-menu-btn"
                aria-label="Menü"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72 bg-background">
              <SheetHeader className="sr-only">
                <SheetTitle>Menü</SheetTitle>
              </SheetHeader>
              <SidebarContent onItemClick={() => setOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <div className="lg:grid lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:flex flex-col border-r border-border bg-card sticky top-0 h-screen">
          <SidebarContent />
        </aside>
        <main className="min-h-screen">
          {/* Bakım modu uyarısı — kalıcı ve her sayfada görünür. Engelleme
              backend'de yapılır (backend/maintenance.py); bu yalnızca
              kullanıcıya durumu açıkça bildirmek içindir. */}
          {maintenanceMode && (
            <div
              className="flex items-start gap-3 border-b-2 border-amber-500 bg-amber-50 px-4 sm:px-8 py-3 text-sm text-amber-900"
              role="status"
              data-testid="maintenance-banner"
            >
              <Wrench className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div>
                <div className="font-semibold">Sistem bakım modunda</div>
                <div className="mt-0.5 text-amber-800">
                  Şu anda yeni kayıt ekleyemez veya mevcut kayıtları
                  değiştiremezsiniz. Lütfen daha sonra tekrar deneyiniz.
                </div>
              </div>
            </div>
          )}
          <Outlet />
        </main>
      </div>

      <Toaster richColors position="top-right" />
    </div>
  );
}
